import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Button from '../../components/ui/Button'
import { searchQuery } from '../../services/api/search'

// 两种搜索模式
const SEARCH_MODES = [
  { value: 'smart', key: 'smart' },       // 智能搜索
  { value: 'keyword', key: 'keyword' },   // 关键词搜索
]

const SearchPage = () => {
  const { t, i18n } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [searchResults, setSearchResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [query, setQuery] = useState(searchParams.get('q') || '')
  const [searchMode, setSearchMode] = useState(searchParams.get('mode') || 'smart')
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  const handleSearch = async (e) => {
    e.preventDefault()
    if (query.trim()) {
      performSearch(searchMode)
    }
  }

  // 点击某个搜索模式按钮：立即用该模式执行搜索（不依赖 setState 异步更新）
  const handleModeSearch = (mode) => {
    setSearchMode(mode)
    if (query.trim()) {
      performSearch(mode)
    }
  }

  const performSearch = async (mode = searchMode) => {
    setLoading(true)
    setError(null)
    try {
      const response = await searchQuery({
        query: query.trim(),
        topK: 100,
        docType: 'product_i18n',
        searchMode: mode,
      })
      // 全部结果显示，不做相关性阈值过滤
      setSearchResults(response || [])
    } catch (err) {
      console.error('搜索失败:', err)
      setError(t('search.error', '搜索失败，请重试'))
    } finally {
      setLoading(false)
    }
  }

  // 只在初始加载时执行一次搜索，后续需要用户点击搜索按钮
  useEffect(() => {
    if (isInitialLoad && query) {
      performSearch()
      setIsInitialLoad(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialLoad, query])

  const handleBack = () => {
    navigate(-1)
  }

  // 解析搜索结果 content，提取标题/简介/作者/出版社
  const parseResultContent = (content) => {
    const lines = (content || '')
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean)

    // 标题：取“产品名称：xxx”行
    const titleLine = lines.find(l => /^产品名称[：:]/.test(l))
    const title = titleLine
      ? titleLine.replace(/^产品名称[：:]\s*/, '').trim()
      : (lines[0] || '')

    // 作者
    const getField = (prefix) => {
      const line = lines.find(l => new RegExp(`^${prefix}[：:]`).test(l))
      return line ? line.replace(new RegExp(`^${prefix}[：:]\\s*`), '').replace(/null$/i, '').trim() : ''
    }
    const author = getField('作者')

    // 简介：取“简介：”之后到“【作者简介】”或“作者：”之前的内容
    let summary = ''
    const introIdx = lines.findIndex(l => /^简介[：:]/.test(l))
    if (introIdx !== -1) {
      const introParts = []
      for (let i = introIdx; i < lines.length; i++) {
        const line = lines[i]
        if (i !== introIdx && (/^【作者简介】/.test(line) || /^作者[：:]/.test(line))) break
        let text = line
        if (i === introIdx) text = text.replace(/^简介[：:]\s*/, '')
        if (text) introParts.push(text)
      }
      summary = introParts.join('\n').trim()
    }

    return { title, summary, author }
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* 返回按钮 */}
      <div className="mb-6">
        <Button
          variant="outline"
          onClick={handleBack}
          className="text-gray-600 border-gray-300 hover:bg-gray-100"
        >
          ← {t('common.back')}
        </Button>
      </div>

      {/* 搜索表单 */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          {t('search.title', '搜索')}
        </h1>
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-2">
          {/* 输入框 */}
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('search.placeholder', '请输入搜索内容')}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          {/* 两个搜索按钮并排 */}
          <div className="flex flex-row gap-2 sm:flex-shrink-0">
            {SEARCH_MODES.map(mode => (
              <button
                key={mode.value}
                type="button"
                onClick={() => handleModeSearch(mode.value)}
                disabled={loading}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  searchMode === mode.value
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                } ${loading ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                {(() => {
                  const translated = t(`search.modes.${mode.key}`)
                  if (translated && translated !== `search.modes.${mode.key}`) return translated
                  return mode.value === 'smart' ? '智能搜索' : '关键词搜索'
                })()}
              </button>
            ))}
          </div>
        </form>
      </div>

      {/* 搜索结果 */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('search.results', '搜索结果')}
        </h2>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('common.loading', '加载中...')}</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="text-red-500 text-4xl mb-4">❌</div>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button onClick={performSearch} className="bg-blue-600 hover:bg-blue-700 text-white">
              {t('common.retry', '重试')}
            </Button>
          </div>
        ) : searchResults.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-4xl mb-4">🔍</div>
            <p className="text-gray-600 mb-6">{t('search.noResults', '未找到相关结果')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {searchResults.map((result, index) => {
              const { title, summary, author } = parseResultContent(result.content)
              const productId = result.metadata?.product_id

              const goDetail = () => {
                if (productId) navigate(`/products/${productId}`)
              }

              return (
                <div
                  key={index}
                  onClick={goDetail}
                  className={`border border-gray-200 rounded-lg p-4 transition-colors ${
                    productId
                      ? 'cursor-pointer hover:border-primary-400 hover:bg-primary-50/40 hover:shadow-sm'
                      : 'opacity-70'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="text-lg font-semibold text-primary-600 group-hover:text-primary-800 mb-2 flex-1">
                      {title}
                    </h3>
                    {productId && (
                      <span className="shrink-0 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap">
                        {t('search.viewDetail', '查看详情')} →
                      </span>
                    )}
                  </div>

                  {author && (
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mb-2">
                      <span>{t('search.author', '作者')}：{author}</span>
                    </div>
                  )}

                  {summary && (
                    <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
                      {summary}
                    </p>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default SearchPage
